import { Client } from '@langchain/langgraph-sdk';
import type { LangChainMessage, LangGraphSendMessageConfig } from '@assistant-ui/react-langgraph';
import { getSelectedAccountId } from '@/client/lib/api';

export type ThreadScope = 'personal' | 'account';

let apiBasePath = '/api/chat';
let extraHeadersProvider: (() => Record<string, string>) | null = null;

export function getApiBasePath(): string {
  return apiBasePath;
}

/** Switch the API base path (called when the active agent/deployment changes). */
export function setActiveBasePath(path: string) {
  apiBasePath = path;
}

/**
 * Configure the chat API: override the base path and inject custom headers
 * (e.g. X-Gaia-Apps-Token) on every LangGraph request.
 * Used by the auth provider to configure API headers.
 */
export function configureChatApi(opts: {
  basePath: string;
  headersProvider: () => Record<string, string>;
}) {
  apiBasePath = opts.basePath;
  extraHeadersProvider = opts.headersProvider;
}

export const createClient = async () => {
  const headers: Record<string, string> = {};
  const accountId = getSelectedAccountId();
  if (accountId) headers['X-Account-Id'] = accountId;
  if (extraHeadersProvider) Object.assign(headers, extraHeadersProvider());
  return new Client({
    apiUrl: `${window.location.origin}${apiBasePath}`,
    defaultHeaders: headers,
  });
};

export const createThread = async (metadata?: Record<string, unknown>) => {
  const client = await createClient();
  return client.threads.create({ metadata });
};

export const getThread = async (threadId: string) => {
  const client = await createClient();
  return client.threads.get(threadId);
};

export const getThreadState = async (threadId: string) => {
  const client = await createClient();
  return client.threads.getState(threadId);
};

export const updateThreadState = async (threadId: string, values: Record<string, unknown>) => {
  const client = await createClient();
  return client.threads.updateState(threadId, { values });
};

export const listThreads = async (
  scope: ThreadScope = 'personal',
  extraMetadata?: Record<string, unknown>,
  limit = 50,
) => {
  const client = await createClient();
  return client.threads.search({ metadata: { scope, ...extraMetadata }, limit });
};

export const deleteThread = async (threadId: string) => {
  const client = await createClient();
  return client.threads.delete(threadId);
};

export const updateThreadMetadata = async (threadId: string, metadata: Record<string, unknown>) => {
  const client = await createClient();
  return client.threads.update(threadId, { metadata });
};

/** Tracks threads whose metadata already has a conversation_title. */
const threadsWithMetadataTitle = new Set<string>();

export function markHasMetadataTitle(threadId: string) {
  threadsWithMetadataTitle.add(threadId);
}

export function hasMetadataTitle(threadId: string): boolean {
  return threadsWithMetadataTitle.has(threadId);
}

export type ThreadStatus = 'idle' | 'busy' | 'interrupted' | 'error';

export type ThreadWithDates = {
  thread_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  status: ThreadStatus;
  user_name?: string;
  user_email?: string;
  user_picture?: string;
  source?: string;
  visibility?: 'public' | 'private';
};

export type ListThreadsOptions = {
  limit: number;
  offset: number;
  sortBy?: 'created_at' | 'updated_at';
  sortOrder?: 'asc' | 'desc';
  status?: ThreadStatus;
  metadata?: Record<string, unknown>;
};

export type ListThreadsResult = {
  threads: ThreadWithDates[];
  totalCount: number;
};

/**
 * Derive a title from a thread's state values, falling back to first human message.
 * The conversation_metadata middleware stores { title } in state values.
 */
function deriveTitleFromState(values: Record<string, unknown> | undefined): { title?: string } {
  const cm = values?.conversation_metadata as { title?: string } | undefined;
  if (cm?.title) return { title: cm.title };

  // Fallback: first human message content
  const messages = (values?.messages ?? []) as LangChainMessage[];
  const firstHuman = messages.find((m) => m.type === 'human');
  if (firstHuman && typeof firstHuman.content === 'string') {
    const text = firstHuman.content.slice(0, 60);
    return { title: firstHuman.content.length > 60 ? text + '...' : text };
  }

  return {};
}

/**
 * Fast thread list with server-side pagination and sorting.
 * Returns threads for the requested page plus the total count for pagination controls.
 */
export const listThreadsWithDates = async (
  opts: ListThreadsOptions = { limit: 10, offset: 0 },
): Promise<ListThreadsResult> => {
  const client = await createClient();

  const searchMetadata: Record<string, unknown> = {
    scope: 'account',
    ...opts.metadata,
  };

  const [results, totalCount] = await Promise.all([
    client.threads.search({
      metadata: searchMetadata,
      limit: opts.limit,
      offset: opts.offset,
      sortBy: (opts.sortBy ?? 'updated_at') as 'created_at' | 'updated_at',
      sortOrder: opts.sortOrder ?? 'desc',
      status: opts.status,
    }),
    client.threads.count({ metadata: searchMetadata, status: opts.status }),
  ]);

  const threads = results.map((t) => {
    const meta = t.metadata as {
      conversation_title?: string;
      user_name?: string;
      user_email?: string;
      user_picture?: string;
      source?: string;
      visibility?: 'public' | 'private';
    };

    const title = meta?.conversation_title || '';
    if (title) markHasMetadataTitle(t.thread_id);

    return {
      thread_id: t.thread_id,
      title: title || 'New Chat',
      created_at: t.created_at,
      updated_at: t.updated_at,
      status: (t.status ?? 'idle') as ThreadStatus,
      user_name: meta?.user_name,
      user_email: meta?.user_email,
      user_picture: meta?.user_picture,
      source: meta?.source,
      visibility: meta?.visibility ?? 'private',
    };
  });

  return { threads, totalCount };
};

/**
 * Resolve a title for a single thread by fetching its state.
 * Returns the derived title, or null if no meaningful title could be derived.
 * Also backfills the title into thread metadata for future fast loads.
 */
export const resolveThreadTitle = async (threadId: string): Promise<string | null> => {
  try {
    const state = await getThreadState(threadId);
    const derived = deriveTitleFromState(state.values as Record<string, unknown>);
    const title = derived.title || null;

    if (title) {
      markHasMetadataTitle(threadId);
      void updateThreadMetadata(threadId, { conversation_title: title }).catch(() => {});
    }
    return title;
  } catch {
    return null;
  }
};

type RunCreatedMeta = {
  run_id: string;
};

const activeRunIdsByThread = new Map<string, Set<string>>();

export function registerThreadRun(threadId: string, runId: string) {
  const runIds = activeRunIdsByThread.get(threadId) ?? new Set<string>();
  runIds.add(runId);
  activeRunIdsByThread.set(threadId, runIds);
}

export function unregisterThreadRun(threadId: string, runId: string) {
  const runIds = activeRunIdsByThread.get(threadId);
  if (!runIds) return;
  runIds.delete(runId);
  if (runIds.size === 0) activeRunIdsByThread.delete(threadId);
}

/**
 * Best-effort server-side cancellation for all currently tracked runs in a thread.
 * This complements assistant-ui's local stream abort so backend work also stops.
 */
export async function cancelTrackedRunsForThread(threadId: string): Promise<void> {
  const client = await createClient();
  const trackedRunIds = [...(activeRunIdsByThread.get(threadId) ?? [])];

  // Fallback: if stream metadata headers were unavailable, discover active runs directly.
  const [runningRuns, pendingRuns] = await Promise.all([
    client.runs.list(threadId, { status: 'running', limit: 20 }),
    client.runs.list(threadId, { status: 'pending', limit: 20 }),
  ]);

  const runIds = new Set<string>(trackedRunIds);
  for (const run of runningRuns) runIds.add(run.run_id);
  for (const run of pendingRuns) runIds.add(run.run_id);
  if (runIds.size === 0) return;

  await Promise.allSettled(
    [...runIds].map(async (runId) => {
      try {
        await client.runs.cancel(threadId, runId, false, 'interrupt');
      } finally {
        unregisterThreadRun(threadId, runId);
      }
    }),
  );
}

/**
 * Find an active run on a thread so we can rejoin its event stream.
 * Returns null if no run is currently running or pending.
 */
export async function discoverActiveRunId(threadId: string): Promise<string | null> {
  const client = await createClient();
  const [running, pending] = await Promise.all([
    client.runs.list(threadId, { status: 'running', limit: 1 }),
    client.runs.list(threadId, { status: 'pending', limit: 1 }),
  ]);
  return running[0]?.run_id ?? pending[0]?.run_id ?? null;
}

/**
 * Reconnect to an in-flight run's SSE stream. Yields the same event shape as
 * `sendMessage`, so the caller can pipe both through the same interceptor.
 */
export async function joinRunStream(
  threadId: string,
  runId: string,
  signal?: AbortSignal,
) {
  const client = await createClient();
  return client.runs.joinStream(threadId, runId, {
    signal,
    streamMode: ['messages', 'values', 'updates', 'custom'],
  });
}

/**
 * Marker used to delimit hidden context appended to user messages.
 * Everything after this marker is sent to the agent but hidden in the chat UI.
 */
export const HIDDEN_CONTEXT_MARKER = '\n<!-- gaia-context -->\n';

/**
 * Sanitize a URL by stripping query parameters and fragments.
 */
function sanitizeUrl(url: string): string {
  return url.split('?')[0].split('#')[0];
}

/**
 * Append a page-context note to the last human message so the agent knows
 * which page the user is viewing. The note is hidden in the chat UI via
 * HIDDEN_CONTEXT_MARKER but visible to the agent.
 *
 * Handles both string content and array content (used when the message
 * carries attachments such as images or PDFs alongside text).
 */
export function withPageContext(
  messages: LangChainMessage[],
  pageUrl?: string,
): LangChainMessage[] {
  if (!pageUrl || messages.length === 0) return messages;

  const cleanUrl = sanitizeUrl(pageUrl);
  if (!cleanUrl) return messages;

  const last = messages[messages.length - 1];
  if (last.type !== 'human') return messages;

  const note = `${HIDDEN_CONTEXT_MARKER}Note: The user is asking this question while viewing the following page: ${cleanUrl}`;

  if (typeof last.content === 'string') {
    return [...messages.slice(0, -1), { ...last, content: last.content + note }];
  }

  if (!Array.isArray(last.content)) return messages;

  // Append the note to the last text part — or add a new text part when the
  // user sent only media (e.g. image with no caption).
  const nextContent = [...last.content];
  let lastTextIdx = -1;
  for (let i = nextContent.length - 1; i >= 0; i--) {
    const part = nextContent[i];
    if (part && typeof part === 'object' && 'type' in part && part.type === 'text') {
      lastTextIdx = i;
      break;
    }
  }
  if (lastTextIdx >= 0) {
    const textPart = nextContent[lastTextIdx] as { type: 'text'; text: string };
    nextContent[lastTextIdx] = { ...textPart, text: textPart.text + note };
  } else {
    nextContent.push({ type: 'text', text: note } as (typeof nextContent)[number]);
  }
  return [...messages.slice(0, -1), { ...last, content: nextContent } as LangChainMessage];
}

export const sendMessage = async (params: {
  threadId: string;
  messages: LangChainMessage[];
  assistantId: string;
  pageUrl?: string;
  agentMetadata?: Record<string, string>;
  files?: Record<string, unknown>;
  signal?: AbortSignal;
  config?: LangGraphSendMessageConfig;
  onRunCreated?: (meta: RunCreatedMeta) => void;
}) => {
  const client = await createClient();
  const isResume = !!params.config?.command;
  const input: Record<string, unknown> | null = isResume
    ? null
    : (() => {
        const i: Record<string, unknown> = {
          messages: withPageContext(params.messages, params.pageUrl),
        };
        if (params.files) i.files = params.files;
        return i;
      })();
  return client.runs.stream(params.threadId, params.assistantId, {
    input,
    context: {
      ...params.agentMetadata,
    },
    streamMode: ['messages', 'values', 'updates', 'custom'],
    streamSubgraphs: true,
    multitaskStrategy: 'interrupt',
    // Buffer events server-side so a reload can rejoin via joinStream(),
    // and keep the run going if the SSE connection drops.
    streamResumable: true,
    onDisconnect: 'continue',
    signal: params.signal,
    ...params.config,
    onRunCreated: params.onRunCreated,
  });
};
